<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { useAlert } from 'dashboard/composables';
import { getEmptyStateContent } from './helpers/emptyState';

import CreateToolSetDialog from './create/CreateToolSetDialog.vue';
import EditToolSetDialog from './edit/EditToolSetDialog.vue';
import DeleteToolSetDialog from './delete/DeleteToolSetDialog.vue';
import CreateToolDialog from './create/CreateToolDialog.vue';
import DeleteToolDialog from './delete/DeleteToolDialog.vue';
import ToolSetCard from './ToolSetCard.vue';
import ExecutionLogDialog from './ExecutionLogDialog.vue';
import Button from 'next/button/Button.vue';
import EmptyStateLayout from 'next/EmptyStateLayout.vue';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';
import { useRoute } from 'vue-router';

const emit = defineEmits(['selectTool']);

const route = useRoute();
const store = useStore();
const { t } = useI18n();

const createToolSetDialogRef = ref(null);
const editToolSetDialogRef = ref(null);
const deleteToolSetDialogRef = ref(null);
const createToolDialogRef = ref(null);
const deleteToolDialogRef = ref(null);
const executionLogDialogRef = ref(null);
const selectedToolSet = ref({});
const selectedTool = ref({});
const fileInput = ref(null);

const botId = computed(() => Number(route.params.botId));
const bot = computed(() => store.getters['wgptBots/getBot'](botId.value));
const uiFlags = useMapGetter('wgptBotTools/getUIFlags');
const toolSetList = computed(() => store.getters['wgptBotTools/getToolSets']);

const hasWebWidgetInbox = computed(() =>
  (bot.value?.inbox_ids || []).some(id => {
    const inbox = store.getters['inboxes/getInbox'](id);
    return inbox?.channel_type === INBOX_TYPES.WEB;
  })
);

const BUILT_IN_ID = 'built-in';

const builtInToolSet = computed(() => ({
  id: BUILT_IN_ID,
  name: t('WGPT_BOTS.EDIT.TABS.TOOLS.BUILT_IN.TITLE'),
  description: t('WGPT_BOTS.EDIT.TABS.TOOLS.BUILT_IN.DESCRIPTION'),
}));

const builtInTools = computed(() => [
  {
    key: 'accessToCurrentDatetime',
    name: t(
      'WGPT_BOTS.EDIT.TABS.TOOLS.BUILT_IN.TOOLS.ACCESS_TO_CURRENT_DATETIME.NAME'
    ),
    description: t(
      'WGPT_BOTS.EDIT.TABS.TOOLS.BUILT_IN.TOOLS.ACCESS_TO_CURRENT_DATETIME.DESCRIPTION'
    ),
    icon: 'i-lucide-clock',
    active: !!bot.value.config?.accessToCurrentDatetime,
    type: 'built_in',
  },
  {
    key: 'accessToPreviousConversations',
    name: t(
      'WGPT_BOTS.EDIT.TABS.TOOLS.BUILT_IN.TOOLS.ACCESS_TO_PREVIOUS_CONVERSATIONS.NAME'
    ),
    description: t(
      'WGPT_BOTS.EDIT.TABS.TOOLS.BUILT_IN.TOOLS.ACCESS_TO_PREVIOUS_CONVERSATIONS.DESCRIPTION'
    ),
    icon: 'i-lucide-message-square',
    active: !!bot.value.config?.accessToPreviousConversations,
    type: 'built_in',
  },
  ...(hasWebWidgetInbox.value
    ? [
        {
          key: 'accessToWidgetDom',
          name: t(
            'WGPT_BOTS.EDIT.TABS.TOOLS.BUILT_IN.TOOLS.ACCESS_TO_WIDGET_DOM.NAME'
          ),
          description: t(
            'WGPT_BOTS.EDIT.TABS.TOOLS.BUILT_IN.TOOLS.ACCESS_TO_WIDGET_DOM.DESCRIPTION'
          ),
          icon: 'i-lucide-mouse-pointer-click',
          active: !!bot.value.config?.accessToWidgetDom,
          type: 'built_in',
        },
      ]
    : []),
]);

const handleBuiltInToggle = async ({ tool, isActive }) => {
  try {
    await store.dispatch('wgptBots/update', {
      id: botId.value,
      config: {
        [tool.key]: Boolean(isActive),
      },
    });
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    const successMessage = t(
      `WGPT_BOTS.EDIT.TABS.TOOLS.EDIT_TOOL.API.${isActive ? 'ACTIVATED' : 'DEACTIVATED'}_SUCCESSFUL`
    );
    useAlert(successMessage);
  } catch (error) {
    const errorMessage =
      error?.message || t('WGPT_BOTS.EDIT.TABS.TOOLS.EDITOR.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const isFetchingToolSets = computed(() => uiFlags.value.isFetching);

const handleCreateToolSet = () => {
  createToolSetDialogRef.value?.open();
};

const handleEditToolSet = toolSet => {
  selectedToolSet.value = toolSet;
  editToolSetDialogRef.value?.open();
};

const handleDeleteToolSet = toolSet => {
  selectedToolSet.value = toolSet;
  deleteToolSetDialogRef.value?.open();
};

const handleToolSetToggle = async (toolSet, active) => {
  try {
    const updateOptions = {
      botId: botId.value,
      id: toolSet.id,
      active,
    };
    await store.dispatch('wgptBotTools/update', updateOptions);
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('WGPT_BOTS.EDIT.TABS.TOOLS.ACTIVE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleCreateTool = toolSet => {
  selectedToolSet.value = toolSet;
  createToolDialogRef.value?.open();
};

const handleEditTool = (toolSet, tool) => {
  emit('selectTool', toolSet, tool);
};

const handleDeleteTool = (toolSet, tool) => {
  selectedToolSet.value = toolSet;
  selectedTool.value = tool;
  deleteToolDialogRef.value?.open();
};

const handleCloneTool = async (toolSet, tool) => {
  try {
    const cloneOptions = {
      botId: botId.value,
      toolSetId: toolSet.id,
      id: tool.id,
    };
    await store.dispatch('wgptBotTools/cloneTool', cloneOptions);

    const successMessage = t(
      'WGPT_BOTS.EDIT.TABS.TOOLS.CLONE_TOOL.API.SUCCESS_MESSAGE'
    );
    useAlert(successMessage);
  } catch (error) {
    const errorMessage =
      error?.message ||
      t('WGPT_BOTS.EDIT.TABS.TOOLS.CLONE_TOOL.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleExportToolSet = async toolSet => {
  try {
    const file = await store.dispatch('wgptBotTools/export', {
      botId: botId.value,
      id: toolSet.id,
    });
    if (!file) return;
    const url = window.URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    a.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    useAlert(t('WGPT_BOTS.EDIT.TABS.TOOLS.EXPORT.API.ERROR_MESSAGE'));
  }
};

const handleExecutionLog = () => {
  executionLogDialogRef.value?.open();
};

const handleImportToolSet = () => {
  fileInput.value?.click();
};

const handleFileChange = async event => {
  try {
    const file = event.target.files[0];
    if (!file) return;

    await store.dispatch('wgptBotTools/import', {
      botId: botId.value,
      file,
    });
    useAlert(t('WGPT_BOTS.EDIT.TABS.TOOLS.IMPORT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('WGPT_BOTS.EDIT.TABS.TOOLS.IMPORT.API.ERROR_MESSAGE'));
  } finally {
    event.target.value = null;
  }
};

const handleToolToggle = async (toolSet, tool, isActive) => {
  try {
    const updateData = {
      botId: botId.value,
      toolSetId: toolSet.id,
      id: tool.id,
      active: isActive,
    };
    await store.dispatch('wgptBotTools/updateTool', updateData);

    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    const successMessage = t(
      `WGPT_BOTS.EDIT.TABS.TOOLS.EDIT_TOOL.API.${isActive ? 'ACTIVATED' : 'DEACTIVATED'}_SUCCESSFUL`
    );
    useAlert(successMessage, { isActive });
  } catch (error) {
    const errorMessage =
      error?.message || t('WGPT_BOTS.EDIT.TABS.TOOLS.EDITOR.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const toolsSummary = computed(() => {
  const dynamicTools = toolSetList.value.flatMap(ts =>
    (ts.tools || []).map(tool => ({
      active: ts.active && tool.active,
    }))
  );
  const all = [...builtInTools.value, ...dynamicTools];
  const total = all.length;
  const active = all.filter(item => item.active).length;
  return { active, total };
});

const expandedCardId = ref(Number(route.query.expandedToolSet) || BUILT_IN_ID);

const toggleExpanded = id => {
  expandedCardId.value = expandedCardId.value === id ? null : id;
};

onMounted(() => {
  store.dispatch('wgptBotTools/get', botId.value);
});
</script>

<template>
  <div class="mx-6 min-h-full">
    <div class="py-4">
      <h3 class="text-lg font-medium text-n-slate-12">
        {{ t('WGPT_BOTS.EDIT.TABS.TOOLS.NAME') }}
      </h3>
      <p class="text-sm text-n-slate-11 mt-1">
        {{ t('WGPT_BOTS.EDIT.TABS.TOOLS.DESCRIPTION') }}
      </p>
    </div>

    <div class="flex items-center justify-end w-full pb-4 gap-2">
      <span class="text-xs font-medium text-n-slate-10">
        {{
          t('WGPT_BOTS.EDIT.TABS.TOOLS.SUMMARY', {
            active: toolsSummary.active,
            total: toolsSummary.total,
          })
        }}
      </span>
      <div class="h-4 w-px bg-n-strong" />
      <Button
        :label="t('WGPT_BOTS.EDIT.TABS.TOOLS.EXECUTION_LOG.TITLE')"
        icon="i-lucide-scroll-text"
        color="slate"
        size="sm"
        @click="handleExecutionLog"
      />
      <Button
        :label="t('WGPT_BOTS.EDIT.TABS.TOOLS.LIST.HEADER.IMPORT_TOOL_SET')"
        :is-loading="uiFlags.isImporting"
        :disabled="uiFlags.isImporting || isFetchingToolSets"
        icon="i-lucide-import"
        color="slate"
        size="sm"
        @click="handleImportToolSet"
      />
      <Button
        :label="t('WGPT_BOTS.EDIT.TABS.TOOLS.LIST.HEADER.CREATE_TOOL_SET')"
        :is-loading="uiFlags.isCreating"
        :disabled="uiFlags.isCreating || isFetchingToolSets"
        size="sm"
        @click="handleCreateToolSet"
      />
    </div>

    <ToolSetCard
      :tool-set="builtInToolSet"
      :is-expanded="expandedCardId === BUILT_IN_ID"
      :built-in-tools="builtInTools"
      built-in
      @toggle="toggleExpanded(BUILT_IN_ID)"
      @toggle-activate-tool="handleBuiltInToggle"
    />

    <div v-if="isFetchingToolSets" class="flex justify-center py-10">
      <SpinnerLoader :size="24" class="text-n-blue-9" />
    </div>
    <div v-else-if="toolSetList.length" class="pb-8">
      <ToolSetCard
        v-for="toolSet in toolSetList"
        :key="toolSet.id"
        :tool-set="toolSet"
        :is-expanded="expandedCardId === toolSet.id"
        :is-updating="uiFlags.isCreating"
        @toggle="toggleExpanded(toolSet.id)"
        @edit="handleEditToolSet(toolSet)"
        @delete="handleDeleteToolSet(toolSet)"
        @activate="handleToolSetToggle(toolSet, true)"
        @deactivate="handleToolSetToggle(toolSet, false)"
        @export="handleExportToolSet(toolSet)"
        @edit-tool="tool => handleEditTool(toolSet, tool)"
        @delete-tool="tool => handleDeleteTool(toolSet, tool)"
        @clone-tool="tool => handleCloneTool(toolSet, tool)"
        @toggle-activate-tool="
          ({ tool, isActive }) => handleToolToggle(toolSet, tool, isActive)
        "
        @create-tool="handleCreateTool(toolSet)"
      />
    </div>
    <div v-else class="w-full py-8 flex flex-col gap-2 items-center">
      <EmptyStateLayout title="" subtitle="">
        <template #empty-state-item>
          <div class="flex flex-col p-px">
            <ToolSetCard
              v-for="(tool, index) of getEmptyStateContent(t)"
              :key="index"
              :tool-set="tool"
            />
          </div>
        </template>
      </EmptyStateLayout>
    </div>

    <DeleteToolSetDialog
      ref="deleteToolSetDialogRef"
      :bot-id="botId"
      :tool="selectedToolSet"
    />

    <EditToolSetDialog
      ref="editToolSetDialogRef"
      :bot-id="botId"
      :tool="selectedToolSet"
    />

    <CreateToolSetDialog ref="createToolSetDialogRef" :bot-id="botId" />

    <CreateToolDialog
      ref="createToolDialogRef"
      :bot-id="botId"
      :tool-set="selectedToolSet"
    />

    <DeleteToolDialog
      ref="deleteToolDialogRef"
      :bot-id="botId"
      :tool-set="selectedToolSet"
      :tool="selectedTool"
    />

    <ExecutionLogDialog
      ref="executionLogDialogRef"
      :bot-id="botId"
      :tool-sets="toolSetList"
    />

    <input
      ref="fileInput"
      type="file"
      accept=".wgpt"
      class="hidden"
      @change="handleFileChange"
    />
  </div>
</template>
